import { useContext, useEffect, useEffectEvent, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { Link } from 'react-router'
import { Divider, Grid, Typography, useMediaQuery } from '@mui/material'

import ChannelBubbleGroup from '../../../components/channel-bubble/channel-bubble-group'
import PauseDuration from '../../../components/pause-duration'
import { AppContext, useHost } from '../../../providers/app-provider'
import Api from '../../../utils/api'
import { getFormattedDateTime, showDeviceMenuItems } from '../../../utils/helpers'
import useSharedStyles from '../../../utils/shared.styles'

import ActivationDetails from './activation-details'
import DeviceField from './device-field'
import DevicePhoto from './device-photo'

const DeviceDetails = ({ device, setDevice }: { device: IDeviceAPIObject; setDevice: React.Dispatch<React.SetStateAction<IDeviceAPIObject | undefined>> }) => {
  const auth = useAuth()
  const sharedClasses = useSharedStyles
  const host = useHost()
  const { tenantId, pageRefreshCounter } = useContext(AppContext)
  const [folderAttributes, setFolderAttributes] = useState<IFolderAttributesType | null>(null)

  const smallScreenMatches = useMediaQuery((theme) => theme.breakpoints.down('sm'))

  const { lastCheckIn, updatedAt, uniqueId, nodeType, area, metadata, folderId, orderNodeId, orderNumber, orderTimeline } = device
  const { vanityName } = nodeType
  const { device_ownership: deviceOwnership } = metadata || {}

  const deviceBuilding = device.building
  const deviceFloor = device.floor
  const deviceLocation = area || metadata?.device_location || ''
  const deviceLocationNotes = metadata?.device_location_note || ''
  const gps: string | undefined = metadata?.device_location_gps || undefined
  // If a Defender Slim vanity ends with H######, it's a 2.0 Slim with LoRa capability, so we add (+LoRa)
  // For all other devices, show the node type vanity name
  const deviceType = nodeType.name === 'leak5s' && device.vanity?.match(/H\d{6}$/) ? `${vanityName} (+LoRa)` : vanityName

  const getFolderAttributes = useEffectEvent(async () => {
    if (!host || !auth.user?.access_token) return
    const res = await Api.get(`${host}/api/v2/folders/${folderId}/attributes`)
    if (res.ok) {
      setFolderAttributes(res.body)
    }
  })
  useEffect(() => {
    if (folderId) {
      getFolderAttributes()
    }
  }, [folderId, host, pageRefreshCounter])

  // Sort order timeline by updatedAt and get the latest status change date
  const latestOrderStatus = orderTimeline?.sort((a, b) => new Date(b.updatedAt).valueOf() - new Date(a.updatedAt).valueOf())[0]

  let orderStatusText = 'N/A'
  if (latestOrderStatus) {
    orderStatusText = `${latestOrderStatus.message} ${getFormattedDateTime(latestOrderStatus.updatedAt, undefined, 'MM/dd/yyyy')}`
  }

  const { resourcesLink } = showDeviceMenuItems(device)

  return (
    <Grid
      container={true}
      sx={{
        gap: 2,
        m: 1,
      }}
    >
      <Grid
        container={true}
        size={smallScreenMatches ? 12 : 5.9}
        sx={{
          mt: 3,
        }}
      >
        <Grid size={smallScreenMatches ? 12 : 3}>
          <DevicePhoto device={device} setDevice={setDevice} />
        </Grid>

        <Grid container={true} spacing={3} size={9}>
          <Grid
            size={12}
            sx={{
              marginTop: smallScreenMatches ? 1 : 0,
            }}
          >
            <Typography variant="body1" component="span">
              {deviceBuilding}
              {((deviceBuilding && deviceFloor) || (deviceBuilding && deviceLocation)) && ' • '}
            </Typography>

            {deviceFloor && (
              <Typography variant="body1" component="span">
                Floor {deviceFloor}
                {deviceFloor && deviceLocation && ' • '}
              </Typography>
            )}

            <Typography variant="body1" component="span">
              {deviceLocation}
            </Typography>

            <Typography variant="body1" component="div" sx={sharedClasses.secondaryText}>
              {deviceLocationNotes}
            </Typography>
          </Grid>

          <PauseDuration device={device} />

          <Grid size={12}>
            <ChannelBubbleGroup device={device} />
          </Grid>
        </Grid>
      </Grid>
      {!smallScreenMatches && (
        <Grid size="auto">
          <Divider orientation="vertical" variant="middle" />
        </Grid>
      )}
      <Grid
        container={true}
        spacing={2}
        size="grow"
        sx={{
          alignContent: 'flex-start',
          my: 0,
        }}
      >
        <DeviceField xs={6} label="Device Model" value={deviceType} resourcesLink={resourcesLink} />
        <DeviceField xs={6} label="Last Check-in" value={lastCheckIn ? getFormattedDateTime(lastCheckIn) : undefined} />
        <DeviceField xs={6} label="Unique Id" value={uniqueId} />
        <DeviceField xs={6} label="Last Updated" value={updatedAt ? getFormattedDateTime(updatedAt) : undefined} />
        <DeviceField xs={6} label="Device Ownership" value={deviceOwnership as string} />
        <DeviceField xs={6} label="Activation Details" value={<ActivationDetails activationApp={folderAttributes?.activationApp} gps={gps} />} />
        {orderNodeId && orderNumber && (
          <DeviceField
            xs={6}
            label="Order #"
            value={
              <Link to={`/tenant/${tenantId}/orders/${orderNodeId}`}>
                {orderNumber}
                <br />
                {orderStatusText}
              </Link>
            }
          />
        )}
      </Grid>
    </Grid>
  )
}

export default DeviceDetails
